import { useState, useEffect, useMemo } from "react";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, addMonths, subMonths } from "date-fns";
import { useDispatch } from "react-redux";
import * as actions from '../../../redux/actions/growthEngine';
import { toast, ToastContainer } from "react-toastify";
import { ModalShell } from "./ModalShell";
import {
    getAccountTodayString,
    calendarDateFromString,
    isAccountCalendarToday,
    toScheduleDateString,
    toAccountScheduleDateString,
    getCampaignMinBudget,
    formatCampaignMoney,
} from '../../../helper/utility';

export const EditCampaignModal = ({
    showEditBudgetModal,
    closeEditBudgetModal,
    initialBudget,
    startDate,
    initialEndDate,
    currency,
    budgetType,
    campaignId,
    adAccountId, setRefreshFlag,
    openFor,
    name,
    status,
    timezone = '',
    spentAmount = 0,
}) => {
    const dispatch = useDispatch();
    const [isVisible, setIsVisible] = useState(showEditBudgetModal);
    const [budget, setBudget] = useState(initialBudget || "");
    const [endDate, setEndDate] = useState(() => {
        if (!initialEndDate) return null;
        return calendarDateFromString(toAccountScheduleDateString(initialEndDate, timezone));
    });
    const [campaignName, setCampaignName] = useState(name || '');
    const [showLoading, setShowLoading] = useState(false);
    const [minDailyBudget, setMinDailyBudget] = useState(null);
    const accountTodayStr = useMemo(
        () => getAccountTodayString(timezone),
        [timezone]
    );
    const startDayStr = useMemo(
        () => toAccountScheduleDateString(startDate, timezone),
        [startDate, timezone]
    );
    const currencyLabel = currency || "USD";
    const minBudget = useMemo(() => Math.ceil(getCampaignMinBudget({
        budgetType,
        minDailyBudget,
        startDate,
        startTime: null,
        endDate: endDate || initialEndDate,
        endTime: null,
        useRemainingDays: true,
        spentAmount,
        timezone,
    }) || 0), [budgetType, minDailyBudget, startDate, endDate, initialEndDate, spentAmount, timezone]);

    useEffect(() => {
        if (showEditBudgetModal) {
            setIsVisible(true);
        } else {
            const timeout = setTimeout(() => setIsVisible(false), 300);
            return () => clearTimeout(timeout);
        }
    }, [showEditBudgetModal]);

    useEffect(() => {
        if (!showEditBudgetModal || !adAccountId) return undefined;
        let cancelled = false;
        (async () => {
            const limits = await dispatch(actions.loadAdAccountMinBudget(adAccountId, toast));
            if (!cancelled && limits?.minDailyBudget) {
                setMinDailyBudget(limits.minDailyBudget);
            }
        })();
        return () => { cancelled = true; };
    }, [showEditBudgetModal, adAccountId, dispatch]);

    // Only bump when the required min changes (dates / budget type), not while typing
    useEffect(() => {
        if (!minBudget || openFor === "name" || openFor === "status") return;
        const current = Number(budget);
        if (!Number.isNaN(current) && current > 0 && current < minBudget) {
            setBudget(minBudget);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [minBudget, openFor]);

    // Date range selection
    const [currentMonth, setCurrentMonth] = useState(() => {
        return calendarDateFromString(getAccountTodayString(timezone)) || new Date();
    });

    useEffect(() => {
        const todayDate = calendarDateFromString(accountTodayStr);
        if (todayDate) setCurrentMonth(todayDate);
    }, [timezone]);

    // Get the start and end of the current month
    const startOfCurrentMonth = startOfMonth(currentMonth);
    const endOfCurrentMonth = endOfMonth(currentMonth);

    // Get all days of the current month
    const daysInMonth = eachDayOfInterval({
        start: startOfCurrentMonth,
        end: endOfCurrentMonth,
    });

    // Navigate to next and previous month
    const goToNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
    const goToPreviousMonth = () => setCurrentMonth(subMonths(currentMonth, 1));

    // Handle date range selection
    const handleDateClick = (date) => {
        const selectedStr = toScheduleDateString(date);

        // cannot select today or past (account timezone)
        if (!selectedStr || selectedStr <= accountTodayStr) {
            toast.warning("Please select a future date in your ad account timezone", {
                position: "top-center",
                theme: "colored",
                hideProgressBar: true,
            });
            return;
        }

        const selectedDate = calendarDateFromString(selectedStr) || date;

        // cannot be before startDate
        if (startDayStr && selectedStr < startDayStr) {
            toast.warning("End date cannot be before start date", {
                position: "top-center",
                theme: "colored",
                hideProgressBar: true,
            });
            return;
        }

        // Set as end date
        setEndDate(selectedDate);
    };
    const isWithinRange = (date) => {
        if (startDayStr && endDate) {
            const day = toScheduleDateString(date);
            const endStr = toScheduleDateString(endDate);
            return day > startDayStr && day < endStr;
        }
        return false;
    };

    const isSelectedStart = (date) => toScheduleDateString(date) === startDayStr;
    const isSelectedEnd = (date) => toScheduleDateString(date) === toScheduleDateString(endDate);

    const handleSave = async () => {
        // Check if values changed
        const budgetChanged = budget !== initialBudget;
        const endDateChanged = endDate
            ? !initialEndDate || new Date(endDate).getTime() !== new Date(initialEndDate).getTime()
            : initialEndDate !== null;
        const nameChanged = campaignName !== name;
        const payload = {
            campaignId,
            adAccountId,
        };

        if (openFor === "name") {
            if (!nameChanged) {
                closeEditBudgetModal();
                return;
            }
        } else if (openFor === "budget" || openFor === "endDate") {
            // If no change, just close the modal
            if (!budgetChanged && !endDateChanged) {
                closeEditBudgetModal();
                return;
            }
            // Validation
            if (budget <= 0) {
                toast.error("Budget must be greater than 0", {
                    position: "top-center",
                    theme: "colored",
                    hideProgressBar: true,
                });
                return;
            }
            if (minBudget && Number(budget) < minBudget) {
                toast.error(
                    budgetType === "lifetime"
                        ? `Minimum lifetime budget for ${currencyLabel} is ${formatCampaignMoney(minBudget, currencyLabel)}. Please increase your budget or decrease the length of your campaign.`
                        : `Minimum daily budget for ${currencyLabel} is ${formatCampaignMoney(minBudget, currencyLabel)}`,
                    { position: "top-center", theme: "colored", hideProgressBar: true }
                );
                setBudget(minBudget);
                return;
            }
            if (endDate && toScheduleDateString(endDate) <= accountTodayStr) {
                toast.error("End date must be in the future", {
                    position: "top-center",
                    theme: "colored",
                    hideProgressBar: true,
                });
                return;
            }
            if (!budgetChanged && !endDateChanged) {
                closeEditBudgetModal();
                return;
            }
        } else if (openFor === 'status') {
            if (endDate && toScheduleDateString(endDate) <= accountTodayStr) {
                toast.error("Campaign completed. you can't pause or unpause.", {
                    position: "top-center",
                    theme: "colored",
                    hideProgressBar: true,
                });
                return;
            }
            if (status === 'ACTIVE' || status === 'PAUSED') {
                payload.status = status === 'PAUSED' ? 'ACTIVE' : 'PAUSED'
            } else {
                return
            }
        }
        // Send only changed values
        if (budgetChanged) payload.budget = budget;
        if (endDateChanged) payload.endTime = endDate;
        if (nameChanged) payload.name = campaignName;
        if (openFor === "budget" || openFor === "endDate") {
            payload.spentAmount = Number(spentAmount) || 0;
        }
        // console.log(payload,'hello payload');
        await dispatch(actions.updateMetaCampaign({ payload, closeEditBudgetModal, setShowLoading, toast, setRefreshFlag }));

    };

    const title = openFor === "budget" || openFor === "endDate"
        ? "Edit Budget & End Date"
        : openFor === "status"
            ? "Edit campaign status"
            : "Edit campaign name";

    const subtitle = openFor === "status"
        ? "Update campaign status with care."
        : openFor === "name"
            ? "Rename this campaign."
            : "Adjust budget and end date before saving.";

    const footer = (
        <div className="flex items-center justify-end gap-3">
            <button
                onClick={closeEditBudgetModal}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
            >
                Cancel
            </button>
            <button
                onClick={handleSave}
                className="flex items-center gap-2 rounded-full bg-theme_clr2 px-4 py-[10px] text-sm font-medium tracking-[0.037] text-white hover:bg-theme_clr4 disabled:opacity-75"
            >
                {showLoading && <i className="bi bi-arrow-repeat animate-spin"></i>}
                {showLoading ? 'Updating campaign' : 'Save'}
            </button>
        </div>
    );

    return (
        <>
            <ToastContainer />
            <ModalShell
                isOpen={showEditBudgetModal}
                isVisible={isVisible}
                onClose={closeEditBudgetModal}
                title={title}
                subtitle={subtitle}
                footer={footer}
            >
                {openFor === "status" && (status === 'ACTIVE' || status === 'PAUSED') ? (
                    <div className="mb-4 flex items-center gap-2 border-l-4 border-red-500 bg-red-100 p-4 text-red-800">
                        <i className="bi bi-exclamation-triangle-fill text-lg"></i>
                        <span><strong>Warning:</strong> Are you sure you want to make {status === 'PAUSED' ? "Activate" : "Paused"} the campaign?</span>
                    </div>
                ) :
                    openFor === "name" ? (
                        <div className="mb-4">
                            <label className="form-label font-medium">Campaign Name</label>
                            <input
                                type="text"
                                value={campaignName}
                                onChange={(e) => setCampaignName(e.target.value)}
                                placeholder="Enter campaign name"
                                className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none"
                            />
                        </div>
                    ) : (
                        <>
                            {/* Budget Input */}
                            <div className="mb-5">
                                <label className="form-label font-medium">Budget</label>
                                <div className="relative mt-2">
                                    <input
                                        type="number"
                                        value={budget}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            // allow empty or partial numbers while typing
                                            if (val === "" || !isNaN(Number(val))) {
                                                setBudget(val);
                                            }
                                        }}
                                        onBlur={() => {
                                            let val = Number(budget);

                                            if (isNaN(val) || budget === "") {
                                                setBudget(minBudget || 1);
                                                return;
                                            }

                                            if (minBudget && val < minBudget) {
                                                setBudget(minBudget);
                                            } else {
                                                setBudget(val);
                                            }
                                        }}
                                        placeholder="Enter budget"
                                        className="w-full rounded-lg border border-gray-300 px-3 py-2 pr-10 focus:outline-none 
                                   [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                    />
                                    {/* Currency on right side */}
                                    <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-500">
                                        {currency}
                                    </span>
                                </div>
                                {minDailyBudget ? (
                                    <div className="mt-2 text-[11px] text-slate-500">
                                        {budgetType === "lifetime"
                                            ? `Minimum lifetime budget for ${currencyLabel} is ${formatCampaignMoney(minBudget, currencyLabel)} (Meta daily min × remaining days${Number(spentAmount) > 0 ? ", and not below amount already spent" : ""}).`
                                            : `Minimum daily budget for ${currencyLabel} is ${formatCampaignMoney(minBudget || minDailyBudget, currencyLabel)}.`}
                                    </div>
                                ) : null}
                            </div>

                            {/* End Date Picker */}
                            <div className="mb-2">
                                <label className="form-label font-medium">End Date</label>
                                <div className="mt-2 rounded-xl border border-theme_clr1 bg-white p-3 sm:p-4">
                                    <div className="calendar-wrapper mx-auto w-full max-w-[260px] text-center sm:max-w-[300px]">

                                        {/* Month Navigation */}
                                        <div className="mb-2 flex items-center justify-between text-[12px] font-medium sm:text-[13px]">
                                            <button className="h-[28px] w-[32px]" onClick={goToPreviousMonth}>
                                                <i className="bi bi-chevron-left"></i>
                                            </button>
                                            <span className="text-sm sm:text-base">{format(currentMonth, "MMM yyyy")}</span>
                                            <button className="h-[28px] w-[32px]" onClick={goToNextMonth}>
                                                <i className="bi bi-chevron-right"></i>
                                            </button>
                                        </div>

                                        {/* Days of the Week */}
                                        <div className="grid grid-cols-7 gap-[2px] text-center">
                                            {["S", "M", "T", "W", "T", "F", "S"].map((d, index) => (
                                                <div key={index} className="flex h-[28px] items-center justify-center text-[11px] font-medium sm:h-[32px] sm:text-xs">
                                                    {d}
                                                </div>
                                            ))}

                                            {/* Empty days before first of month */}
                                            {Array.from({ length: startOfCurrentMonth.getDay() }).map((_, i) => (
                                                <div key={i}></div>
                                            ))}

                                            {/* Days of month */}
                                            {daysInMonth.map((date) => {
                                                const dayStr = toScheduleDateString(date);
                                                const isPastOrToday = dayStr <= accountTodayStr;
                                                return (
                                                    <div
                                                        key={date}
                                                        onClick={() => handleDateClick(date)}
                                                        className={`text-theme_clr1 text-[11.5px] sm:text-sm 
      h-[28px] w-[28px] sm:h-[32px] sm:w-[32px] 
      flex items-center justify-center rounded-full cursor-pointer transition
      ${isSelectedStart(date) ? "bg-theme_clr1 text-white" : ""}
      ${isSelectedEnd(date) ? "bg-theme_clr2 text-white" : ""}
      ${isWithinRange(date) ? "bg-blue-100" : ""}
      ${isAccountCalendarToday(date, timezone) ? "ring-2 ring-yellow-400" : ""}
      ${isPastOrToday ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-200"}`}
                                                    >
                                                        {format(date, "d")}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
            </ModalShell>
        </>
    );
};
